/**
 * Validation utilities for production steps consistency checks.
 *
 * Data structure reference (recipes.json):
 *   values.sections[].productionSteps[] = {
 *     reusable: bool,
 *     step: {
 *       index: UUID,
 *       name: string,
 *       stepNumber: number,
 *       endStepWeighing: bool,
 *       netWeight: number,
 *       stepComponents: [           // absent when reusable=true
 *         {
 *           supplierItem: obj|null, // null when coming from a prior step
 *           priorSteps: {           // null when base ingredient
 *             index: UUID,          // references the source step
 *             stepComponents: [...] // snapshot of the source step's components
 *           }|null,
 *           sectionOutput: obj|null // third source type (% of a section)
 *         }
 *       ],
 *       productionSteps: [...]      // when reusable=true: inner steps
 *     }
 *   }
 */

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

/**
 * Flatten all steps in a section's productionSteps into a single array.
 * For reusable entries (reusable=true) we descend into step.productionSteps.
 */
function getAllStepsInSection(sectionProductionSteps) {
  const allSteps = []
  for (const entry of sectionProductionSteps || []) {
    const step = entry.step
    if (!step) continue
    if (entry.reusable && Array.isArray(step.productionSteps)) {
      for (const innerStep of step.productionSteps) {
        allSteps.push(innerStep)
      }
    } else {
      allSteps.push(step)
    }
  }
  return allSteps
}

/**
 * Build the set of step indices that are referenced as priorSteps
 * in any stepComponent of any step in the provided list.
 */
function buildReferencedStepIndices(allSteps) {
  const referenced = new Set()
  for (const step of allSteps) {
    for (const comp of step.stepComponents || []) {
      if (comp.priorSteps && comp.priorSteps.index) {
        referenced.add(comp.priorSteps.index)
      }
    }
  }
  return referenced
}

/**
 * Returns true when a stepComponent has at least one valid source:
 *   - a supplierItem
 *   - a priorSteps reference
 *   - a sectionOutput reference
 */
function componentHasSource(comp) {
  return !!(
    comp.supplierItem ||
    (comp.priorSteps && comp.priorSteps.index) ||
    comp.sectionOutput
  )
}

// ─────────────────────────────────────────────
// Validation 1 — Chain coherence
// ─────────────────────────────────────────────

/**
 * Checks:
 *  a) Every step has at least one prior step defined (unless all its components
 *     are base / sectionOutput → it is the first in its chain).
 *  b) Every derived step (has at least one priorSteps component) that is NOT
 *     the final step of the section is referenced by a downstream step.
 */
function validateChainCoherence(sectionProductionSteps) {
  const errors = []
  const allSteps = getAllStepsInSection(sectionProductionSteps)
  if (allSteps.length === 0) return errors

  const referencedIndices = buildReferencedStepIndices(allSteps)
  const maxStepNumber = Math.max(...allSteps.map(s => s.stepNumber ?? -1))

  for (const step of allSteps) {
    const components = step.stepComponents || []
    const stepName = step.name || "Étape sans nom"

    // ── a) priorStep check ──────────────────────────────────────────────────
    // If any component has no source at all, the step is missing a prior link.
    // We only flag this when at least one component explicitly expects a prior
    // step (supplierItem is null) but has no priorSteps or sectionOutput set.
    // Components with netWeight === 0 and no source are treated as empty
    // placeholders (ghost rows not displayed in the UI) and are skipped.
    const isMissingSource = c =>
      !c.supplierItem &&
      !c.sectionOutput &&
      !(c.priorSteps && c.priorSteps.index) &&
      c.netWeight > 0
    const hasMissingPrior = components.some(isMissingSource)
    if (hasMissingPrior) {
      const indexOfMissing = components.findIndex(isMissingSource)
      console.error("Step with missing prior:", step, "Missing component:", components[indexOfMissing], "index :", indexOfMissing)
      errors.push(`L'étape "${stepName}" n'a pas d'étape précédente définie`)
    }

    // ── b) ulteriorStep check ───────────────────────────────────────────────
    // A derived step that is not the final step should be referenced downstream.
    const isDerived = components.some(c => c.priorSteps && c.priorSteps.index)
    const isReferenced = referencedIndices.has(step.index)
    const isFinalStep = (step.stepNumber ?? -1) === maxStepNumber

    if (isDerived && !isReferenced && !isFinalStep) {
      console.error("Step with missing ulterior:", step)
      errors.push(`L'étape "${stepName}" n'a pas d'étape suivante définie`)
    }
  }

  return errors
}

// ─────────────────────────────────────────────
// Validation 2 — Ingredient coherence
// ─────────────────────────────────────────────

/**
 * Checks for ghost ingredients: when step M references step A via priorSteps,
 * every base ingredient (supplierItem) declared in step A must appear in the
 * priorSteps snapshot stored in M's component.
 * Matching is done by supplierItem id (or objectId as fallback).
 *
 * Note: orphaned components (no source at all) are already reported by
 * validateChainCoherence and are skipped here.
 */
function validateIngredientCoherence(sectionProductionSteps) {
  const errors = []
  const allSteps = getAllStepsInSection(sectionProductionSteps)
  if (allSteps.length === 0) return errors

  // Build lookup map: step.index → step
  const stepByIndex = new Map(allSteps.map(s => [s.index, s]))

  for (const step of allSteps) {
    const components = step.stepComponents || []
    const stepName = step.name || "Étape sans nom"

    for (const comp of components) {
      // Skip components with no source (already reported by validateChainCoherence)
      // and components with no priorSteps reference (nothing to check here).
      if (!componentHasSource(comp) || !comp.priorSteps || !comp.priorSteps.index) continue

      // ── No ghost ingredients ─────────────────────────────────────────────

      const sourceStep = stepByIndex.get(comp.priorSteps.index)
      if (!sourceStep) continue // referenced step not in this section – skip

      // Base ingredients of the source step (direct supplierItems)
      const sourceBaseComponents = (sourceStep.stepComponents || []).filter(
        sc => sc.supplierItem
      )

      // Build a set of supplier ids present in the priorSteps snapshot
      const snapshotSupplierIds = new Set(
        (comp.priorSteps.stepComponents || [])
          .filter(sc => sc.supplierItem)
          .map(sc => sc.supplierItem.id || sc.supplierItem.objectId)
          .filter(Boolean)
      )

      for (const sourceComp of sourceBaseComponents) {
        const ingId =
          sourceComp.supplierItem.id || sourceComp.supplierItem.objectId
        if (ingId && !snapshotSupplierIds.has(ingId)) {
          const ingName =
            sourceComp.supplierItem.name || "ingrédient inconnu"
          errors.push(
            `L'ingrédient "${ingName}" n'est pas rappelé dans l'étape ultérieure "${stepName}"`
          )
          console.error(`Step with missing ingredient reference: ${stepName}`, step)
        }
      }

      // ── Reverse check: snapshot ingredient not produced by source step ──────
      const sourceSupplierIds = new Set(
        sourceBaseComponents
          .map(sc => sc.supplierItem.id || sc.supplierItem.objectId)
          .filter(Boolean)
      )

      for (const snapshotComp of (comp.priorSteps.stepComponents || [])) {
        if (!snapshotComp.supplierItem) continue
        const snapshotIngId =
          snapshotComp.supplierItem.id || snapshotComp.supplierItem.objectId
        if (snapshotIngId && !sourceSupplierIds.has(snapshotIngId)) {
          const ingName = snapshotComp.supplierItem.name || "ingrédient inconnu"
          errors.push(
            `L'ingrédient "${ingName}" utilisé dans "${stepName}" n'est pas produit par une étape précédente`
          )
          console.error(`Snapshot ingredient not in source step: ${ingName}`, step)
        }
      }
    }
  }

  return errors
}

// ─────────────────────────────────────────────
// Validation 3 — Weight coherence
// ─────────────────────────────────────────────

const WEIGHT_EPSILON = 0.001 // 1 g tolerance

/**
 * The final steps of each section are those with endStepWeighing=true that are
 * not referenced by any downstream step.  Their netWeight sum must match the
 * recipe's declared netWeight (values.netWeight).
 */
// eslint-disable-next-line no-unused-vars
function validateWeightCoherence(values) {
  const errors = []
  let sumFinalNetWeights = 0

  for (const section of values.sections || []) {
    const allSteps = getAllStepsInSection(section.productionSteps)
    if (allSteps.length === 0) continue

    const referencedIndices = buildReferencedStepIndices(allSteps)

    const finalSteps = allSteps.filter(
      s => s.endStepWeighing && !referencedIndices.has(s.index)
    )

    for (const s of finalSteps) {
      sumFinalNetWeights += s.netWeight || 0
    }
  }

  const recipeNetWeight = values.netWeight || 0
  if (Math.abs(recipeNetWeight - sumFinalNetWeights) > WEIGHT_EPSILON) {
    const toG = w => Math.round(w * 1000)
    errors.push(
      `Le poids final de la recette (${toG(recipeNetWeight)}g) ne correspond pas aux pesées finales (${toG(sumFinalNetWeights)}g)`
    )
  }

  return errors
}

// ─────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────

/**
 * Run all consistency validations on a production-steps form payload.
 * Returns an array of human-readable error strings (empty = valid).
 */
export function validateProductionStepsConsistency(values) {
  const errors = []

  for (const section of values.sections || []) {
    const productionSteps = section.productionSteps || []
    errors.push(...validateChainCoherence(productionSteps))
    errors.push(...validateIngredientCoherence(productionSteps))
  }

  // errors.push(...validateWeightCoherence(values))
  //already calculated

  return errors
}
